import { useMemo, useEffect, useState } from 'react'
import { Tooltip } from 'gokwik-ui-kit'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { getApprovalStatus, getMerchantDetails, getUserDetails } from '@store/user/selectors'
import { newStepConfiguration } from '@pages/onboarding/stepsConfig'
import { useAppDispatch } from '@library/utilities/hooks'
import { makeAPICall } from '@gokwik/utilities'
import incompleteStep from "@library/images/icons/incompleteStep.svg";
import blueCheck from "@library/images/icons/completeStep.svg";
import redirect from "@library/images/icons/redirectionLink.svg";
import sparkles from '@library/images/icons/sparkles.svg'
import openLock from '@library/images/icons/lock-open.svg'
import tooltipCancel from '@library/images/icons/cancel-tooltip.svg'
import progressIcon from "@library/images/icons/progressIcon.svg";
import APIEndPoints from '@library/utilities/constants/apiEndpoints'
import InstallKwikCheckout from './installKwikCheckoutModal'
import { logEvent } from '@library/utilities/userLogsEvents/userLogEvents'
import { fetchBillingStatus } from '@store/user'

export default function () {
    const merchant_details = useSelector(getMerchantDetails)
    const billingSubscriptionData = useSelector(getApprovalStatus);
    const approvedChargesStatus = billingSubscriptionData?.status
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [isInstallModalVisible, setIsInstallModalVisible] = useState(false);
    const [codShippingRate, setCodShippingRate] = useState(false)
    const [isCountryIndia, setIsCountryIndia] = useState(true)
    const [currentOnboardingStatus, setCurrentOnboardingStatus] = useState('')
    const [isTooltipVisible, setIsTooltipVisible] = useState(false)
    const [currentSlide, setCurrentSlide] = useState(0)
    const [isKyc, setIsKyc] = useState(true)
    const [remainingTrialDays, setRemainingTrialDays] = useState(0)
    const [isTrialExpired, setIsTrialExpired] = useState(false)
    const showKycNudgeStatusList = [
        'kyc_viewed',
        'pre_kyc_entity_type',
        'pre_kyc_business_category',
        'kyc_document_upload',
        'kyc_review_submit',
        'kyc_cpv_completed',
        'kyc_verification_pending',
        'kyc_verification_failed',
    ]
    const slideInterval = 2000
    const manualKwikCheckoutButtonAdded = merchant_details?.kwik_checkout_manual_btn_added

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slideMessages.length)
        }, slideInterval)

        return () => clearInterval(interval)
    }, [])

    const slideMessages = useMemo(() => {
        const trialMessage = isTrialExpired
            ? `Your trial period is expired`
            : remainingTrialDays === 0
            ? 'Your trial period will expire today'
            : `${remainingTrialDays} days left in your trial period`

        return [
            isKyc && merchant_details?.kyc_completion_status?.toLowerCase() !== 'approved'
                ? 'Complete KYC to avoid extra charges'
                : 'Install to avoid extra charges',
            trialMessage,
        ]
    }, [remainingTrialDays, isKyc, merchant_details, isTrialExpired])

    const checkIfChargesApprovedOnce = (onboardingStatus: string) => {
        if (newStepConfiguration[onboardingStatus]?.id < newStepConfiguration['gk_shopify_charges_approved']?.id) {
            sessionStorage.setItem("billingStatusFetched", "false");
        }
        if ((sessionStorage.getItem("billingStatusFetched") === 'false' || !sessionStorage.getItem("billingStatusFetched")) && newStepConfiguration[onboardingStatus]?.id >= newStepConfiguration['gk_shopify_charges_approved']?.id) {
            dispatch(fetchBillingStatus())
            sessionStorage.setItem("billingStatusFetched", "true");
        }
    }

    useEffect(() => {
        const fetchData = () => {
            checkIfChargesApprovedOnce(merchant_details?.onboarding_status?.kwik_checkout_app)
            setCurrentOnboardingStatus(merchant_details?.onboarding_status?.kwik_checkout_app)
            if (merchant_details?.onboarding_status?.kwik_checkout_app === 'kwik_checkout_app_uninstalled') {
                setIsInstallModalVisible(true);
            } else if (isInstallModalVisible) {
                setIsInstallModalVisible(false)
            }
        }
        fetchData()
    }, [merchant_details?.onboarding_status?.kwik_checkout_app])

    const fetchFreeTrialDays = async () => {
        try {
            const response = await makeAPICall({
                method: 'get',
                url: `${process.env.REACT_APP_BASE_URL}${APIEndPoints.getRemainingTrialDays}?app=kwik_checkout`,
            })
            if (response?.data?.status_code === 200) {
                const today = new Date().getTime()
                const trialEnd = new Date(response?.data?.data?.trialEndDate).getTime()
                setIsTrialExpired(response?.data?.data?.trialDays === 0 && today > trialEnd)
                setRemainingTrialDays(response?.data?.data?.trialDays)
            }
        } catch (error) {
            console.log(error)
        }
    }

    const user_details = useSelector(getUserDetails)

    const isKycCompleted = () => {
        return merchant_details?.kyc_completion_status?.toLowerCase() === 'approved'
    }

    const [userJourneySteps, setUserJourneySteps] = useState(() => {
        const steps = [
            { id: 'cod-activation', text: 'Activate GoKwik COD' },
            { id: 'enable-gk-checkout', text: 'Enable GoKwik Checkout' },
            { id: 'payments-checkout-config', text: 'Enable Payment Options' },
        ]
        if (!isKycCompleted()) {
            steps.splice(2, 0, { id: 'kyc-verification', text: 'Verify Your Business (KYC)' })
        }
        return steps
    })

    useEffect(() => {
        fetchFreeTrialDays()
    }, [])

    useEffect(() => {
        if (currentOnboardingStatus && !showKycNudgeStatusList?.includes(currentOnboardingStatus)) setIsKyc(false)
    }, [currentOnboardingStatus])

    useEffect(() => {
        if (merchant_details?.country && merchant_details?.country?.toLowerCase() !== 'india') {
            setIsCountryIndia(false)
            setUserJourneySteps([{ id: 'cod-activation', text: 'Activate GoKwik COD' }])
        }
    }, [merchant_details.country])
    
    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        setCodShippingRate(params?.get('cod-shipping-rate') === 'true' || false)

        const hasDismissed = localStorage.getItem('gkCheckoutTooltipDismissed')
        if (!hasDismissed) {
            setIsTooltipVisible(true)
        }
    }, [])

    const handleCloseTooltip = () => {
        setIsTooltipVisible(false)
        localStorage.setItem('gkCheckoutTooltipDismissed', 'true')
    }

    const stepStatuses = useMemo(() => {
        const statuses: Record<string, string> = {}
        let currentStatus = merchant_details?.onboarding_status?.kwik_checkout_app
        const codModuleStatus = merchant_details?.onboarding_status?.cod_module_status;
        const gkCheckoutStatus = merchant_details?.onboarding_status?.gk_checkout_status;
        const kycStatus = merchant_details?.onboarding_status?.kyc_status
        const gkPaymentsStatus = merchant_details?.onboarding_status?.gk_payments_status

        userJourneySteps.forEach((step) => {
            if (step.id === 'cod-activation') {
                if (newStepConfiguration[currentStatus]?.id >= newStepConfiguration['imp_checks_passed']?.id || codModuleStatus === 'imp_checks_passed') {
                    statuses[step.id] = 'Completed';
                } else {
                    statuses[step.id] = 'Ongoing';
                }
            } else if (step.id === 'enable-gk-checkout') {
                if ( gkCheckoutStatus==='gk_checkout_buttons_not_working' && !manualKwikCheckoutButtonAdded ) {
                    statuses[step.id] = 'Pending';
                } else if (
                    newStepConfiguration[currentStatus]?.id >=
                    newStepConfiguration['gk_checkout_buttons_not_working']?.id &&
                    approvedChargesStatus === 'APPROVED' || gkCheckoutStatus === 'gk_checkout_buttons_working' ||
                    newStepConfiguration[currentStatus]?.id > newStepConfiguration['kyc_verification_failed']?.id
                ) {
                    statuses[step.id] = 'Completed'
                } else if (gkCheckoutStatus === '' && currentStatus !== 'imp_checks_passed') {
                    statuses[step.id] = 'Yet To Start'
                } else {
                    statuses[step.id] = '30 Days free Trial'
                }
            } else if (step.id === 'kyc-verification') {
                if (newStepConfiguration[currentStatus]?.id >= newStepConfiguration['kyc_completed']?.id || ['setup_completed', 'kyc_completed'].includes(kycStatus)) {
                    statuses[step.id] = 'Completed'
                } else if (currentStatus === 'kyc_verification_pending') {
                    statuses[step.id] = 'In-progress • ETA 2-3 Weeks'
                } else if (currentStatus === 'kyc_verification_failed') {
                    statuses[step.id] = 'KYC Verification Failed'
                } else if (
                    newStepConfiguration[currentStatus]?.id <
                    newStepConfiguration['gk_checkout_buttons_not_working']?.id
                ) {
                    statuses[step.id] = 'Yet To Start'
                } else {
                    statuses[step.id] = 'slider'
                }
            } else if (step.id === 'payments-checkout-config') {
                if (newStepConfiguration[currentStatus]?.id >=
                    newStepConfiguration['gk_payments_app_installed']?.id || gkPaymentsStatus === 'gk_payments_app_installed') {
                    statuses[step.id] = 'Completed';
                } else if (newStepConfiguration[currentStatus]?.id <
                    newStepConfiguration['kyc_completed']?.id && !(isKycCompleted() && newStepConfiguration[currentStatus]?.id >=
                    newStepConfiguration['gk_checkout_buttons_not_working']?.id)) {
                    statuses[step.id] = 'Yet To Start';
                } else {
                    statuses[step.id] = 'slider'
                }
            }
        })

        return statuses
    }, [currentOnboardingStatus])

    const callLogEvent = (step: string) => {
        logEvent(
            "progress_stepper_clicked",
            'click',
            'onboarding-steps',
            user_details?.email,
            merchant_details?.m_id,
            merchant_details?.short_name,
            user_details?.name,
            {Step : step},
        )
    }

    const handleRedirection = (id: string) => {
        if (id === 'cod-activation') {
            callLogEvent('cod-activation')
            navigate('/onboarding/cod-setup')
        } else if (id === 'enable-gk-checkout') {
            callLogEvent('enable-gokwik-checkout')
            navigate('/onboarding/checkout-setup')
        } else if (id === 'kyc-verification') {
            callLogEvent('kyc-verification')
            navigate('/onboarding/kyc')
        } else if (id === 'payments-checkout-config') {
            callLogEvent('payments-activation')
            navigate('/onboarding/payment-setup')
        }
    }

    const getCardStyle = (status) => {
        if (
            status === 'KYC Verification Failed' ||
            status === 'Install to avoid extra charges' ||
            status === 'slider'
        ) {
            return {
                containerStyle: {
                    border: '1px solid red',
                    borderTop: '2px solid red',
                    background: '#ffe6e6',
                    fontWeight: '600',
                },
                textStyle: { color: 'red', fontSize: '12px', fontWeight: '400' },
            }
        } else if (
            status === 'Ongoing' ||
            status === 'In-progress • ETA 2-3 Weeks' ||
            status === '30 Days free Trial'
        ) {
            return {
                containerStyle: {
                    border: '1px solid #1677FF',
                    borderTop: '2px solid blue',
                    background: '#eff6fe',
                    fontWeight: '600',
                    color: 'black',
                },
                textStyle: { color: 'rgba(0, 75, 141, 1)', fontSize: '12px', fontWeight: '400' },
            }
        } else if (status === 'Completed') {
            return {
                containerStyle: {
                    border: '0.2px solid rgba(0,0,0,0.3)',
                    background: '#fff',
                    color: 'rgba(0, 0, 0, 0.70)',
                    fontWeight: '400',
                },
                textStyle: { color: 'rgba(10, 91, 57, 1)', fontSize: '12px', fontWeight: '400' },
            }
        } else if (status === 'Yet To Start') {
            return {
                containerStyle: { border: '0.2px solid rgba(0,0,0,0.3)', background: '#fff', color: 'rgba(0,0,0,0.7)' },
                textStyle: { color: 'rgba(0,0,0,0.7)', fontSize: '12px', fontWeight: '400' },
            }
        } else if(status === 'Pending') {
            return {
                containerStyle: { border: '0.2px solid #FEE089', background: '#FFFAEB', color: 'rgba(0,0,0,0.7)' },
                textStyle: { color: 'rgba(0,0,0,0.7)', fontSize: '12px', fontWeight: '400' },
            }
        } else {
            return {
                containerStyle: { border: '0.2px solid rgba(0,0,0,0.3)', background: '#fff' },
                textStyle: { color: 'rgba(0,0,0,0.7)', fontSize: '12px', fontWeight: '400' },
            }
        }
    }

    const showToolTipConditions = (itemId: string) => {
        return (
            itemId === 'enable-gk-checkout' &&
            stepStatuses[itemId] !== 'Completed' &&
            stepStatuses[itemId] !== 'Pending' &&
            stepStatuses[itemId] !== 'Yet To Start' &&
            newStepConfiguration[currentOnboardingStatus]?.id <= newStepConfiguration['kyc_verification_failed']?.id &&
            newStepConfiguration[currentOnboardingStatus]?.id >= newStepConfiguration['kyc_viewed']?.id
        )
    }

    return (
        <>
        {!codShippingRate &&
            ![
                'gk_additional_payment_methods_configured',
                'gk_payments_app_installed',
                'merchant_onboarded',
            ].includes(currentOnboardingStatus) &&
            isCountryIndia &&
            merchant_details?.onboarding_status?.kwik_checkout_app && (
                <div className='w-full'>
                <div
                    className='w-[full] bg-[#fff] mr-2 mb-2'
                    style={{ padding: '0.6rem 1rem', borderRadius: '0.5rem' }}
                >
                    <div
                        style={{ boxSizing: 'border-box' }}
                        className='flex justify-between items-center flex-wrap text-[14px] w-[full] gap-2'
                    >
                        {userJourneySteps?.map((item, index) => {
                            const { containerStyle, textStyle } = getCardStyle(stepStatuses[item.id])
                            const button = (
                                <button
                                    key={index}
                                    style={{
                                        ...containerStyle,
                                        flex: '1',
                                        boxSizing: 'border-box',
                                        padding: '12px',
                                        textAlign: 'left',
                                        borderRadius: '6px',
                                    }}
                                    className='flex items-center p-[10px] gap-1 text-[14px]'
                                    onClick={() => handleRedirection(item.id)}
                                    disabled={
                                        stepStatuses[item.id] === 'Completed' ||
                                        stepStatuses[item.id] === 'Yet To Start' ||
                                        stepStatuses[item.id] === 'Pending'
                                    }
                                >
                                    {stepStatuses[item.id] === 'Completed' ? (
                                        <img src={blueCheck} className='w-[25px] h-[25px]' />
                                    ) : stepStatuses[item.id]==='Pending' ? (
                                        <img src={progressIcon} className='w-[25px] h-[25px]' />
                                    ) : stepStatuses[item.id] === 'KYC Verification Failed' ? (
                                        <img src={incompleteStep} className='w-[25px] h-[25px]' />
                                    ) : stepStatuses[item.id] === 'Yet To Start' ? (
                                        <div
                                            className='w-[25px] h-[25px] flex items-center justify-center text-[rgba(0, 0, 0, 0.45)]'
                                            style={{ background: 'rgb(240, 240, 240)', borderRadius: '50%' }}
                                        >
                                            {index + 1}
                                        </div>
                                    ) : (
                                        <div
                                            className='w-[33px] h-[25px] flex items-center justify-center text-[#fff]'
                                            style={{ background: '#1677FF', borderRadius: '50%' }}
                                        >
                                            {index + 1}
                                        </div>
                                    )}
                                    <div className='flex flex-col gap-1 ml-[10px] w-full'>
                                        <div>{item.text}</div>
                                        {stepStatuses[item.id] === '30 Days free Trial' ? (
                                            <div className='inline-block'>
                                                <img
                                                    className='w-4 h-4 animate-pulse opacity-0 scale-90 transition-all duration-1000 ease-in-out'
                                                    src={sparkles}
                                                />
                                                <span
                                                    style={{
                                                        ...textStyle,
                                                        background:
                                                            'linear-gradient(90deg, rgba(222, 238, 255, 0) 0%, #B6DEFF 100%)',
                                                        padding: '2px 4px',
                                                    }}
                                                >
                                                    {stepStatuses[item.id]}
                                                </span>
                                            </div>
                                        ) : (
                                            <div style={textStyle}>
                                                {stepStatuses[item.id] === 'slider' ? (
                                                    <>
                                                        <div className='flex justify-center gap-1'>
                                                            {slideMessages.map((_, index) => (
                                                                <div
                                                                    key={index}
                                                                    style={{
                                                                        backgroundColor: `${
                                                                            currentSlide === index ? 'red' : ''
                                                                        }`,
                                                                        border: '0.5px solid red',
                                                                    }}
                                                                    className='h-1 w-1 rounded-full transition-all duration-300'
                                                                />
                                                            ))}
                                                        </div>

                                                        {slideMessages[currentSlide]}
                                                    </>
                                                ) : (
                                                    stepStatuses[item.id]
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {stepStatuses[item.id] !== 'Completed' &&
                                        stepStatuses[item.id] !== 'Pending' &&
                                        stepStatuses[item.id] !== 'Yet To Start' && (
                                            <div className='redirect ml-[auto]'>
                                                <img style={{ cursor: 'pointer' }} src={redirect} />
                                            </div>
                                        )}
                                </button>
                            )

                            return showToolTipConditions(item.id) ? (
                                <Tooltip
                                    key={index}
                                    title={
                                        <div className='flex items-center text-[12px] text-bold'>
                                            <img className='mr-2' src={openLock} />
                                            <span>Step unlocked, now you can enable the GoKwik Checkout</span>
                                            <div onClick={handleCloseTooltip} className='ml-2'>
                                                <img src={tooltipCancel} />
                                            </div>
                                        </div>
                                    }
                                    open={isTooltipVisible}
                                    placement='bottom'
                                    overlayInnerStyle={{
                                        backgroundColor: 'black',
                                        color: 'white',
                                        width: 400,
                                        borderRadius: '0.5rem',
                                        padding: '0.5rem',
                                    }}
                                    overlayStyle={{
                                        zIndex: 1,
                                    }}
                                >
                                    {button}
                                </Tooltip>
                            ) : (
                                button
                            )
                        })}
                    </div>
                    <InstallKwikCheckout isInstallModalVisible={isInstallModalVisible} setIsInstallModalVisible={setIsInstallModalVisible}/>
                </div>
                </div>
            )}
        </>
    )
}
